import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { post, get, patch } from '../../../../helpers/httpClient';
import { toast } from 'react-toastify';
import ImageUploader from '../../../../helpers/ImageUploader';
import PageBasicFields from '../../../../helpers/PageBasicFields';

function normalizeColumns(columns) {
  const cols = Array.isArray(columns) ? columns : [];
  return cols.map((col) => ({
    properties: Array.isArray(col?.properties)
      ? col.properties.map((p) => {
          const type = ['image', 'text', 'line'].includes((p?.type || '').toLowerCase()) ? (p.type || '').toLowerCase() : 'text';
          let meta = null;
          if (type === 'line') {
            const m = (p && p.meta && typeof p.meta === 'object') ? p.meta : {};
            const fs = String(m.fontSize ?? '').toLowerCase();
            const allowedFontSizes = ['heading', 'sub-heading', 'text'];
            meta = {
              prefix: String(m.prefix ?? ''),
              suffix: String(m.suffix ?? ''),
              fontSize: allowedFontSizes.includes(fs) ? fs : 'text',
            };
          }
          return {
            name: String(p?.name ?? ''),
            type,
            required: !!p?.required,
            show_name: p?.show_name === undefined ? true : !!p.show_name,
            as_title: !!p?.as_title,
            as_subtitle: !!p?.as_subtitle,
            meta,
          };
        })
      : [],
  }));
}

function GalleryItemEditor() {
  const navigate = useNavigate();
  const { id, pageId } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = !!pageId;

  const [form, setForm] = useState({ slug: '', title: '', preview: null, template: null, extra: { props: {} } });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [parentPage, setParentPage] = useState(null);
  const [structure, setStructure] = useState({ columns: [] });

  const titleText = useMemo(() => (isEdit ? 'Edit Gallery item' : 'New Gallery item'), [isEdit]);

  async function loadTemplateColumns(websiteId, galleryTemplateId) {
    if (!galleryTemplateId) return [];
    const resp = await get(`websites/${websiteId}/gallery-templates/${galleryTemplateId}`);
    return resp?.gallery_template?.columns ?? [];
  }

  // Load current item (when editing) and determine parent, then load gallery template to get structure
  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        setError('');
        if (isEdit) {
          const itemResp = await get(`pages/${pageId}`);
          const item = itemResp?.page || itemResp;
          if (!item) throw new Error('Item not found');
          if (!mounted) return;
          setForm({
            slug: item.slug || '',
            title: item.title || '',
            description: item.description || '',
            caption: item.caption || '',
            preview: item.preview || null,
            template: item.template || null,
            components: item.components || { items: [] },
            extra: item.extra || { props: {} }
          });
          const parentId = item.parent?.id ?? item.parent;
          if (parentId) {
            try {
              const parentResp = await get(`pages/${parentId}`);
              const parent = parentResp?.page || parentResp;
              if (mounted) {
                setParentPage(parent);
                const columns = await loadTemplateColumns(id, parent?.extra?.galleryTemplateId);
                if (mounted) setStructure({ columns: normalizeColumns(columns) });
              }
            } catch {
              if (mounted) toast.warn('Could not load parent gallery layout structure.');
            }
          }
        } else {
          // creating new: load parent by id from query
          const parentId = searchParams.get('parent');
          if (parentId) {
            const resp = await get(`pages/${parentId}`);
            const parent = resp?.page || resp;
            if (!parent) throw new Error('Parent gallery not found');
            if (mounted) {
              setParentPage(parent);
              const columns = await loadTemplateColumns(id, parent?.extra?.galleryTemplateId);
              if (mounted) setStructure({ columns: normalizeColumns(columns) });
            }
          } else {
            toast.warn('No parent selected. Please navigate from a gallery or provide a parent parameter.');
          }
        }
      } catch (e) {
        if (mounted) setError('Failed to load data.');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [id, pageId, isEdit, searchParams]);

  const handleChangeProp = (name, value) => {
    setForm(prev => ({ ...prev, extra: { ...(prev.extra || {}), props: { ...(prev.extra?.props || {}), [name]: value } } }));
  };

  const handleClearProp = (name) => {
    setForm(prev => {
      const newProps = { ...(prev.extra?.props || {}) };
      delete newProps[name];
      return { ...prev, extra: { ...(prev.extra || {}), props: newProps } };
    });
  };

  const validateRequired = () => {
    const props = form?.extra?.props || {};
    const missing = [];
    for (const col of structure.columns || []) {
      for (const p of col.properties || []) {
        if (p.required) {
          const v = props[p.name];
          if (p.type === 'image') {
            if (!v || typeof v !== 'string' || v.trim() === '') missing.push(p.name);
          } else {
            if (v === undefined || v === null || String(v).trim() === '') missing.push(p.name);
          }
        }
      }
    }
    return missing;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.title.trim()) {
      toast.error('Title is required.');
      return;
    }
    const missing = validateRequired();
    if (missing.length > 0) {
      toast.error(`Please fill required fields: ${missing.join(', ')}`);
      return;
    }

    // For as_title props not explicitly overridden, use caption as the saved value.
    const finalProps = { ...(form.extra?.props || {}) };
    for (const col of structure.columns || []) {
      for (const p of col.properties || []) {
        if (p.as_title && !(p.name in finalProps)) {
          finalProps[p.name] = (form.caption || '').trim();
        }
      }
    }

    const payload = {
      type: 'gallery-item',
      slug: (form.slug || '').trim(),
      title: (form.title || '').trim(),
      description: (form.description || '').trim() || null,
      caption: (form.caption || '').trim() || null,
      preview: form.preview || null,
      template: form.template || null,
      components: form.components || [],
      extra: { ...(form.extra || {}), props: finalProps },
    };

    try {
      setSaving(true);
      setError('');
      if (isEdit) {
        await patch(`websites/${id}/pages/${pageId}`, payload);
      } else {
        const parent = searchParams.get('parent');
        await post(`websites/${id}/pages`, { ...payload, order: 0, parent: parent || null });
      }
      navigate(`/websites/${id}?tab=structure`);
    } catch (err) {
      toast.error(isEdit ? 'Failed to update page.' : 'Failed to create page.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => navigate(-1);

  const propsMap = form?.extra?.props || {};

  const renderPropertyInput = (p) => {
    const key = p.name;
    if (!key) return null;

    const labelEl = (
      <Form.Label className="item-prop-label">
        <span className="item-prop-label-name">
          {p.name}
          {p.required && <span className="item-prop-required" title="Required">*</span>}
        </span>
        <span className={`item-prop-type-badge item-prop-type-badge--${p.type}`}>{p.type}</span>
      </Form.Label>
    );

    switch (p.type) {
      case 'image':
        return (
          <div key={key} className="item-prop">
            {labelEl}
            {propsMap[key] ? (
              <div className="item-prop-image-card">
                <img src={propsMap[key]} alt={key} className="item-prop-img-preview" />
                <div className="item-prop-image-actions">
                  <ImageUploader websiteId={id} label="Replace image" onUploaded={(resp) => handleChangeProp(key, resp.url)} />
                  <button type="button" className="item-prop-image-remove" onClick={() => handleChangeProp(key, '')}>Remove</button>
                </div>
              </div>
            ) : (
              <ImageUploader websiteId={id} onUploaded={(resp) => handleChangeProp(key, resp.url)} />
            )}
          </div>
        );
      case 'text':
        return (
          <div key={key} className="item-prop">
            {labelEl}
            <Form.Control
              as="textarea"
              rows={4}
              value={propsMap[key] || ''}
              onChange={(e) => handleChangeProp(key, e.target.value)}
              placeholder="Enter text…"
            />
          </div>
        );
      case 'line':
      default: {
        const prefix = p.meta?.prefix || '';
        const suffix = p.meta?.suffix || '';
        const hasAffix = prefix || suffix;
        const isTitleMirror = p.as_title;
        const isOverridden = isTitleMirror && (key in propsMap);
        const displayValue = isTitleMirror && !isOverridden ? (form.caption || '') : (propsMap[key] || '');
        return (
          <div key={key} className="item-prop">
            {labelEl}
            <Form.Control
              type="text"
              value={displayValue}
              onChange={(e) => {
                const v = e.target.value;
                if (isTitleMirror && v === (form.caption || '')) {
                  handleClearProp(key);
                } else {
                  handleChangeProp(key, v);
                }
              }}
              placeholder={isTitleMirror ? 'mirrors caption' : p.name}
              className={isTitleMirror && !isOverridden ? 'item-prop-mirrored' : ''}
            />
            {hasAffix && (
              <div className="item-prop-hint">
                {prefix && <span>prefix: <em>"{prefix}"</em></span>}
                {prefix && suffix && <span> · </span>}
                {suffix && <span>suffix: <em>"{suffix}"</em></span>}
              </div>
            )}
          </div>
        );
      }
    }
  };

  return (
    <Container className="mt-4">
      <Row>
        <Col>
              <div className="structure-node-header mb-3">
                <h2 className="m-0">{titleText}</h2>
                <span className="page-type-badge page-type-badge--item">Gallery Item</span>
              </div>

              {error && <Alert variant="danger">{error}</Alert>}

              {loading ? (
                <div className="text-center my-3"><Spinner animation="border" /></div>
              ) : (
                <Form onSubmit={handleSubmit}>
                  {!parentPage && (
                    <Alert variant="warning">Parent gallery is not set. Please go back and add an item under a gallery.</Alert>
                  )}

                  <PageBasicFields
                    form={form}
                    setForm={setForm}
                    websiteId={id}
                    slugPlaceholder="e.g. product-123"
                    titlePlaceholder="Enter title"
                    preserveEmptySlug={isEdit}
                  />

                  <div className="props-panel mt-3">
                    <div className="item-props-header">
                      <span className="item-props-title">Item properties</span>
                      {parentPage && (
                        <span className="item-props-source">from: <strong>{parentPage.title || parentPage.slug}</strong></span>
                      )}
                    </div>
                    {structure.columns.length === 0 ? (
                      <p className="muted-hint mb-0">Parent gallery has no item structure defined.</p>
                    ) : (
                      <div className={`item-props-columns${structure.columns.length > 1 ? ' item-props-columns--multi' : ''}`}>
                        {structure.columns.map((col, idx) => (
                          <div key={idx} className="item-props-column">
                            {structure.columns.length > 1 && (
                              <div className="item-props-column-label">Column {idx + 1}</div>
                            )}
                            {col.properties.length === 0 ? (
                              <p className="muted-hint mb-0">No properties defined</p>
                            ) : (
                              <div className="item-props-grid">
                                {col.properties.map((p) => renderPropertyInput(p))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="form-actions">
                    <Button type="submit" className="primary-button" disabled={saving}>
                      {saving ? (<><Spinner animation="border" size="sm" className="me-2" /> Saving...</>) : 'Save'}
                    </Button>
                    <Button className="primary-button" onClick={handleCancel} disabled={saving}>Cancel</Button>
                  </div>
                </Form>
              )}
        </Col>
      </Row>
    </Container>
  );
}

export default GalleryItemEditor;
